/**
 * One dialog for all five config tables - the form is built from the field contract the server
 * shipped, not written out per table.
 *
 * WHAT IT REFUSES TO GUESS. Company and country come from the page's own selectors and are
 * never offered as a choice: they are already decided, and a form that lets you save a dch rule
 * while looking at dbi is a bug waiting for a quiet month. They are hidden and taken from the
 * props (`own-registration` is the exception - there the country is an ordinary column, because
 * that table's key is (company, posting group) and one company holds AT, CH, DE and FR rows).
 *
 * WHAT IT SAYS BEFORE SAVING. A rule change is retroactive: every month is recomputed from the
 * current rules on every page load. So the dialog asks the server what this key actually touches
 * and prints it - how many months carry postings on the combination, how many of those are still
 * open, and how many are already filed. The filed ones matter too: their FILED figures are
 * frozen, but the computed side moves, so "gemeldet gegen berechnet" changes for them. That is
 * frequently the whole point of the change and never something to find out afterwards.
 *
 * WHAT IT MIRRORS AND WHAT IT DOES NOT DECIDE. The cross-field rules below are the same ones
 * `config-admin.ts` enforces, so the user sees the problem while typing instead of after a
 * round trip. The server still decides; this is a courtesy, not a gate.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { vatReportingApi } from '../../services/vatReportingApi';
import { useVatLang } from './VatLang';
import type {
    ConfigFieldContract, ConfigImpactResponse, ConfigResource, ConfigResourceContract,
    ConfigRow, ConfigRowValue, ConfigWriteResult, VatCompany,
} from '../../types/vatReporting';

interface Props {
    resource: ConfigResource;
    contract: ConfigResourceContract;
    /** 'create' also covers "duplicate": key fields open, values prefilled. */
    mode: 'create' | 'edit';
    initial: ConfigRow;
    company: VatCompany;
    country: string;
    kennzahlOptions: { kennzahl: string; label: string }[];
    /** Field order in the dialog. Anything not listed follows in contract order. */
    order?: string[];
    /** `config.field.<name>` by default; a section can override a label key here. */
    labelKeyFor?: (name: string) => string;
    onClose: () => void;
    onSaved: (result: ConfigWriteResult) => void;
}

/** The two fields the page has already decided, unless the table treats country as a value. */
function hiddenFields(contract: ConfigResourceContract): Set<string> {
    return new Set(contract.countryInKey ? ['companyCode', 'countryCode'] : ['companyCode']);
}

export function ConfigRowDialog({
    resource, contract, mode, initial, company, country, kennzahlOptions,
    order, labelKeyFor, onClose, onSaved,
}: Props) {
    const { t, tOr } = useVatLang();
    const hidden = useMemo(() => hiddenFields(contract), [contract]);

    const [row, setRow] = useState<ConfigRow>(() => {
        const seeded: ConfigRow = { ...initial };
        // Always from the page, never from the row that was clicked.
        seeded.companyCode = company;
        if (contract.countryInKey) seeded.countryCode = country;
        return seeded;
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [impact, setImpact] = useState<ConfigImpactResponse | null>(null);
    const [impactLoading, setImpactLoading] = useState(false);

    const fields = useMemo(() => {
        const byName = new Map(contract.fields.map(f => [f.name, f]));
        const ordered: ConfigFieldContract[] = [];
        for (const name of order ?? []) {
            const f = byName.get(name);
            if (f && !hidden.has(name)) { ordered.push(f); byName.delete(name); }
        }
        for (const f of contract.fields) {
            if (!hidden.has(f.name) && byName.has(f.name)) ordered.push(f);
        }
        return ordered;
    }, [contract, order, hidden]);

    const label = useCallback((name: string) => {
        const key = labelKeyFor ? labelKeyFor(name) : `config.field.${name}`;
        return tOr(key, name);
    }, [labelKeyFor, tOr]);

    const setField = (f: ConfigFieldContract, value: ConfigRowValue) => {
        setRow(prev => {
            const next: ConfigRow = { ...prev, [f.name]: value };
            // Switching a mapping row away from 'mapped' clears the two fields that must then
            // be empty, instead of showing an error about state the user did not choose.
            if (resource === 'line-map' && f.name === 'disposition' && value !== 'mapped') {
                next.kennzahl = null;
                next.lineRole = null;
            }
            if (resource === 'line-map' && f.name === 'lineRole' && value !== 'acquisition') {
                next.kennzahlAcqTax = null;
            }
            return next;
        });
    };

    // --- the cross-field rules, mirrored from config-admin.ts ------------------------------
    const problems = useMemo(() => {
        const out: string[] = [];
        if (resource !== 'line-map') return out;
        const mapped = row.disposition === 'mapped';
        if (mapped && (row.kennzahl === null || row.kennzahl === undefined || row.kennzahl === '')) {
            out.push(t('config.rule.mappedNeedsKennzahl'));
        }
        if (mapped && (row.lineRole === null || row.lineRole === undefined || row.lineRole === '')) {
            out.push(t('config.rule.mappedNeedsLineRole'));
        }
        if (row.lineRole === 'acquisition'
            && (row.kennzahlAcqTax === null || row.kennzahlAcqTax === undefined || row.kennzahlAcqTax === '')) {
            out.push(t('config.rule.acquisitionNeedsAcqKennzahl'));
        }
        if (row.lineRole === 'acquisition' && (row.taxRatePct === null || row.taxRatePct === undefined)) {
            out.push(t('config.rule.acquisitionNeedsRate'));
        }
        if (row.lineRole === 'output_taxable' && (row.taxRatePct === null || row.taxRatePct === undefined)) {
            out.push(t('config.rule.outputTaxableNeedsRate'));
        }
        return out;
    }, [resource, row, t]);

    // --- what this key touches --------------------------------------------------------------
    const busGroup = typeof row.vatBusPostingGroup === 'string' ? row.vatBusPostingGroup : undefined;
    const prodGroup = typeof row.vatProdPostingGroup === 'string' ? row.vatProdPostingGroup : undefined;
    const direction = typeof row.direction === 'string' ? row.direction : undefined;
    const impactable = (resource === 'line-map' || resource === 'scope') && !!busGroup;

    useEffect(() => {
        if (!impactable) { setImpact(null); return; }
        let cancelled = false;
        setImpactLoading(true);
        // Debounced: the business group is a free-text field on a create, and one request per
        // keystroke against the replica is not a thing to do to a shared database.
        const timer = window.setTimeout(() => {
            vatReportingApi.getConfigImpact({
                company, country, resource,
                vatBusPostingGroup: busGroup,
                vatProdPostingGroup: resource === 'line-map' ? prodGroup : undefined,
                direction: resource === 'line-map' ? direction : undefined,
            })
                .then(r => { if (!cancelled) setImpact(r); })
                // A failed impact lookup must not block the save - it is information, not a gate.
                .catch(() => { if (!cancelled) setImpact(null); })
                .finally(() => { if (!cancelled) setImpactLoading(false); });
        }, 400);
        return () => { cancelled = true; window.clearTimeout(timer); };
    }, [impactable, company, country, resource, busGroup, prodGroup, direction]);

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload: ConfigRow = { ...row, companyCode: company };
            if (contract.countryInKey) payload.countryCode = country;
            const result = mode === 'create'
                ? await vatReportingApi.createConfigRow(resource, payload)
                : await vatReportingApi.updateConfigRow(resource, payload);
            onSaved(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSaving(false);
        }
    };

    const enumOptions = (f: ConfigFieldContract): { value: string; label: string }[] => {
        // The two Kennzahl fields get the server's list WITH the official form wording, so the
        // dropdown reads like the form rather than like a number.
        if (f.name === 'kennzahl' || f.name === 'kennzahlAcqTax') {
            return kennzahlOptions.map(o => ({ value: o.kennzahl, label: `${o.kennzahl} — ${o.label}` }));
        }
        return (f.values ?? []).map(v => ({
            value: String(v),
            label: tOr(`config.value.${f.name}.${String(v)}`, String(v)),
        }));
    };

    const renderField = (f: ConfigFieldContract) => {
        const disabled = f.key && mode === 'edit';
        const value = row[f.name];
        const common = 'w-full border rounded px-2 py-1.5 text-sm bg-white disabled:bg-gray-100 disabled:text-gray-500';

        if (f.kind === 'bool') {
            return (
                <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={value === true} disabled={disabled}
                           onChange={e => setField(f, e.target.checked)}
                           className="rounded border-gray-300" />
                    <span className="text-gray-700">{label(f.name)}</span>
                </label>
            );
        }
        if (f.kind === 'enum') {
            return (
                <select className={common} disabled={disabled}
                        value={value === null || value === undefined ? '' : String(value)}
                        onChange={e => {
                            const raw = e.target.value;
                            if (raw === '') { setField(f, null); return; }
                            // A numeric enum has to go back as a number - `sign` is SMALLINT.
                            const numeric = (f.values ?? []).every(v => typeof v === 'number');
                            setField(f, numeric ? Number(raw) : raw);
                        }}>
                    {(f.nullable || value === null || value === undefined) && (
                        <option value="">{t('config.notSet')}</option>
                    )}
                    {enumOptions(f).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            );
        }
        if (f.kind === 'number') {
            return (
                <input type="number" className={common} disabled={disabled}
                       step={f.decimals ? 1 / 10 ** f.decimals : 1}
                       value={value === null || value === undefined ? '' : String(value)}
                       onChange={e => setField(f, e.target.value === '' ? null : Number(e.target.value))} />
            );
        }
        return (
            <input type="text" className={common} disabled={disabled}
                   maxLength={f.maxLength ?? undefined}
                   placeholder={f.allowEmpty ? t('config.emptyAllowed') : undefined}
                   value={value === null || value === undefined ? '' : String(value)}
                   onChange={e => setField(f, e.target.value)} />
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-auto">
            <div className="bg-white rounded shadow-xl w-full max-w-2xl my-8">
                <div className="flex items-start justify-between gap-3 px-5 py-3 border-b">
                    <div>
                        <h3 className="font-semibold text-gray-900">
                            {mode === 'create' ? t('config.dialogCreate') : t('config.dialogEdit')}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {t(`config.section.${resource}.title`)} · {company.toUpperCase()}
                            {contract.countryInKey ? ` / ${country}` : ''} · {contract.table}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="px-5 py-4 space-y-3">
                    {mode === 'edit' && (
                        <p className="text-xs text-gray-500">{t('config.keyLocked')}</p>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        {fields.map(f => (
                            <div key={f.name} className={f.name === 'note' ? 'col-span-2' : ''}>
                                {f.kind === 'bool' ? (
                                    <div className="pt-5">{renderField(f)}</div>
                                ) : (
                                    <>
                                        <label className="block text-xs text-gray-500 mb-1">
                                            {label(f.name)}
                                            {f.key && <span className="ml-1 text-gray-400">{t('config.keyMarker')}</span>}
                                        </label>
                                        {renderField(f)}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>

                    {problems.length > 0 && (
                        <div className="border border-amber-200 bg-amber-50 rounded p-3 text-sm text-amber-900">
                            <div className="flex items-center gap-1.5 font-medium mb-1">
                                <AlertTriangle className="h-4 w-4" />{t('config.rulesUnmet')}
                            </div>
                            <ul className="list-disc pl-5 space-y-0.5">
                                {problems.map(p => <li key={p}>{p}</li>)}
                            </ul>
                        </div>
                    )}

                    {impactable && (
                        <div className="border rounded p-3 text-sm bg-gray-50">
                            <div className="font-medium text-gray-800 mb-1">{t('config.impactTitle')}</div>
                            {impactLoading && (
                                <div className="text-gray-500 flex items-center gap-1.5">
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />{t('page.loading')}
                                </div>
                            )}
                            {!impactLoading && impact && impact.periods.length === 0 && (
                                <p className="text-gray-600">{t('config.impactNone', { from: impact.fromYear })}</p>
                            )}
                            {!impactLoading && impact && impact.periods.length > 0 && (
                                <>
                                    <p className="text-gray-700">
                                        {t('config.impactSummary', {
                                            months: impact.periods.length,
                                            entries: impact.entryCount,
                                            open: impact.openCount,
                                            filed: impact.filedCount,
                                            from: impact.fromYear,
                                        })}
                                    </p>
                                    {impact.filedCount > 0 && (
                                        <p className="text-amber-800 mt-1">{t('config.impactFiledNote')}</p>
                                    )}
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {impact.periods.map(p => (
                                            <span key={`${p.year}-${p.month}`}
                                                  title={t('config.impactPeriodTitle', {
                                                      entries: p.entryCount,
                                                      status: tOr(`status.${p.status}`, p.status),
                                                  })}
                                                  className={`px-1.5 py-0.5 rounded text-xs border ${
                                                      p.status === 'filed'
                                                          ? 'bg-white border-gray-300 text-gray-600'
                                                          : 'bg-blue-50 border-blue-300 text-blue-800'
                                                  }`}>
                                                {p.year}-{String(p.month).padStart(2, '0')}
                                            </span>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {error && (
                        <div className="border border-red-200 bg-red-50 rounded p-3 text-sm text-red-800">
                            {error}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-gray-50">
                    <button onClick={onClose} disabled={saving}
                            className="px-3 py-1.5 text-sm rounded border bg-white hover:bg-gray-100">
                        {t('config.cancel')}
                    </button>
                    <button onClick={save} disabled={saving || problems.length > 0}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        {t('config.save')}
                    </button>
                </div>
            </div>
        </div>
    );
}
